import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertCircleIcon, RefreshCwIcon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Display, Text } from '@newcore/ui/typography';
import { errorTracker } from '@/common/error-tracking';

interface Props {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error tracking service
    if (errorInfo.componentStack) {
      errorTracker.captureReactError(error, {
        componentStack: errorInfo.componentStack,
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback && this.state.error) {
        return this.props.fallback(this.state.error);
      }

      return (
        <div className='min-h-screen flex items-center justify-center bg-primary p-4'>
          <div className='max-w-md w-full space-y-4'>
            <div className='flex items-center justify-center'>
              <AlertCircleIcon className='h-12 w-12 text-error-primary' />
            </div>

            <div className='text-center space-y-2'>
              <Display as='h1' size={DisplaySizes.XS} weight={FontWeights.BOLD}>
                Something went wrong
              </Display>
              <Text as='p' className='text-tertiary'>
                We encountered an unexpected error. Please try refreshing the
                page.
              </Text>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className='mt-4 p-4 bg-secondary rounded-lg'>
                <Text as='p' className='font-mono text-error-primary mb-2'>
                  {this.state.error.toString()}
                </Text>
                {this.state.errorInfo && (
                  <Text
                    as='details'
                    size={TextSizes.XS}
                    className='text-tertiary'
                  >
                    <summary className='cursor-pointer hover:text-primary'>
                      View stack trace
                    </summary>
                    <pre className='mt-2 whitespace-pre-wrap'>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </Text>
                )}
              </div>
            )}

            <div className='flex justify-center'>
              <Button
                onClick={this.handleReset}
                size={Sizes.LG}
                iconLeading={RefreshCwIcon}
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
